"use client"

import React from "react"
import { X } from "lucide-react"

interface ModalProps {
  isVisible: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  isLoading?: boolean
  size?: "sm" | "md" | "lg" | "xl"
  /** Override header background/border classes */
  headerClassName?: string
  /** Title/close color. Defaults to dark when headerClassName is set, else light (white on brand). */
  headerTone?: "light" | "dark"
}

export function Modal({
  isVisible,
  onClose,
  title,
  children,
  isLoading,
  size = "md",
  headerClassName,
  headerTone,
}: ModalProps) {
  if (!isVisible) return null

  const sizeClasses = {
    sm: "sm:max-w-sm",
    md: "sm:max-w-2xl",
    lg: "sm:max-w-4xl",
    xl: "sm:max-w-6xl",
  }

  const resolvedTone =
    headerTone ||
    (headerClassName &&
    (headerClassName.includes("bg-white") ||
      headerClassName.includes("bg-slate") ||
      headerClassName.includes("E8EEF1") ||
      headerClassName.includes("F1F0E8") ||
      headerClassName.includes("bg-gray"))
      ? "dark"
      : "light")

  const isDarkText = resolvedTone === "dark"

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex min-h-[100dvh] items-end justify-center px-0 pt-0 pb-0 text-center sm:block sm:min-h-screen sm:p-0">
        <div
          className="fixed inset-0 z-0 bg-black/45 backdrop-blur-[2px] transition-opacity duration-300 ease-in-out"
          aria-hidden="true"
          onClick={onClose}
        />

        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div
          className={`relative z-10 inline-flex max-h-[100dvh] w-full flex-col overflow-hidden rounded-none border border-slate-300/70 bg-slate-100 text-left align-bottom shadow-2xl transition-all duration-300 ease-out sm:my-8 sm:max-h-[calc(100vh-4rem)] sm:w-full sm:rounded-xl sm:align-middle ${sizeClasses[size]} animate-in fade-in zoom-in-95 duration-300`}
        >
          <div
            className={`flex shrink-0 items-center justify-between border-b px-5 py-3.5 sm:px-6 ${
              headerClassName || "bg-[#2A5C8A] border-[#1e4568]"
            }`}
          >
            <h3
              className={`text-base font-semibold tracking-tight sm:text-lg ${
                isDarkText ? "text-slate-800" : "text-white"
              }`}
              id="modal-title"
            >
              {title}
            </h3>
            <button
              onClick={onClose}
              className={`rounded-md p-1.5 transition-colors duration-150 focus:outline-none focus:ring-2 ${
                isDarkText
                  ? "text-slate-500 hover:bg-black/5 hover:text-slate-800 focus:ring-slate-200"
                  : "text-white/80 hover:bg-white/10 hover:text-white focus:ring-white/30"
              }`}
              disabled={isLoading}
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-100">
            <div className="custom-scrollbar flex-1 overflow-y-auto px-5 py-5 sm:px-6">
              {React.Children.map(children, (child) => {
                if (React.isValidElement(child) && child.type === "form") {
                  return React.cloneElement(child, { isLoading })
                }
                return child
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
